import React, { useState, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { getAccessToken } from '@/api/adapters/SupabaseAdapter';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import {
  Sparkles, MapPin, Loader2, CheckCircle2,
  Search, Filter, LayoutList, ChevronRight, ChevronLeft, Phone,
  AlertTriangle, RefreshCw, Target
} from 'lucide-react';
import { findDuplicateLead, getCategoryColor, getCategoryIcon, calculateLeadScore, categorizeByScore } from '@/components/utils/LeadCategorization';
import { notifyBulkLeadImport } from '@/components/notifications/notificationHelper';
import { toast } from 'sonner';

// ─── Branş tanımları ───────────────────────────────────────────────────────
const DISPLAY_BRANCHES = [
  'Asiatische Küche', 'Bowl/Smoothies', 'Burger', 'Döner',
  'Indische Küche', 'Italienische Küche', 'Hotel', 'Industrie',
  'Bäckerei', 'Café', 'Pizzeria', 'Sushi', 'Steakhaus', 'Griechische Küche', 'Türkische Küche',
];

const BRANCHEN_MAP = {
  'asiatische küche': 'Asiatische Küche',
  'bowl': 'Bowl/Smoothies',
  'burger': 'Burger',
  'döner': 'Döner',
  'indische': 'Indische Küche',
  'italienische': 'Italienische Küche',
  'hotel': 'Hotel',
  'industrie': 'Industrie',
  'bäckerei': 'Bäckerei',
  'café': 'Café',
  'pizzeria': 'Pizzeria',
  'sushi': 'Sushi',
  'steakhaus': 'Steakhaus',
  'griechische': 'Griechische Küche',
  'türkische': 'Türkische Küche',
};

// ─── Branş-bazlı arama konfigürasyonu ────────────────────────────────────────
const BRANCH_SEARCH_CONFIG = {
  'Asiatische Küche': {
    primarySearch: { keywords: ['asiatisches restaurant', 'asian restaurant'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['chinesisches restaurant', 'chinese restaurant'], type: 'restaurant' },
      { keywords: ['thai restaurant', 'vietnamesisches restaurant'], type: 'restaurant' },
    ],
  },
  'Bowl/Smoothies': {
    primarySearch: { keywords: ['bowl smoothie', 'acai bowl'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['smoothie bar', 'juice bar'], type: 'cafe' },
      { keywords: ['healthy food', 'poke bowl'], type: 'restaurant' },
    ],
  },
  'Burger': {
    primarySearch: { keywords: ['burger restaurant', 'burger'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['burger laden', 'burgers'], type: 'meal_takeaway' },
    ],
  },
  'Döner': {
    primarySearch: { keywords: ['döner kebab', 'döner'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['kebab', 'kebab imbiss'], type: 'meal_takeaway' },
      { keywords: ['türkischer imbiss'], type: 'restaurant' },
    ],
  },
  'Indische Küche': {
    primarySearch: { keywords: ['indisches restaurant', 'indian restaurant'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['curry restaurant', 'tandoori'], type: 'restaurant' },
    ],
  },
  'Italienische Küche': {
    primarySearch: { keywords: ['italienisches restaurant', 'italian restaurant'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['ristorante', 'trattoria'], type: 'restaurant' },
      { keywords: ['pasta restaurant'], type: 'restaurant' },
    ],
  },
  'Hotel': {
    primarySearch: { keywords: ['hotel'], type: 'lodging' },
    fallbackSearches: [
      { keywords: ['pension', 'gasthof'], type: 'lodging' },
      { keywords: ['boutique hotel', 'aparthotel'], type: 'lodging' },
    ],
  },
  'Industrie': {
    primarySearch: { keywords: ['gastronomie grosshandel', 'food service'], type: null },
    fallbackSearches: [
      { keywords: ['catering unternehmen', 'catering service'], type: null },
      { keywords: ['grossküche', 'betriebsrestaurant'], type: 'restaurant' },
    ],
  },
  'Bäckerei': {
    primarySearch: { keywords: ['bäckerei', 'bakery'], type: 'bakery' },
    fallbackSearches: [
      { keywords: ['backstube', 'konditorei'], type: 'bakery' },
    ],
  },
  'Café': {
    primarySearch: { keywords: ['café', 'cafe'], type: 'cafe' },
    fallbackSearches: [
      { keywords: ['coffee shop', 'kaffeehaus'], type: 'cafe' },
    ],
  },
  'Pizzeria': {
    primarySearch: { keywords: ['pizzeria', 'pizza'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['pizza lieferservice', 'pizza delivery'], type: 'meal_delivery' },
    ],
  },
  'Sushi': {
    primarySearch: { keywords: ['sushi restaurant', 'sushi'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['sushi bar', 'japanisches restaurant'], type: 'restaurant' },
    ],
  },
  'Steakhaus': {
    primarySearch: { keywords: ['steakhouse', 'steakhaus'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['steak restaurant', 'grill restaurant'], type: 'restaurant' },
    ],
  },
  'Griechische Küche': {
    primarySearch: { keywords: ['griechisches restaurant', 'greek restaurant'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['taverna', 'gyros'], type: 'restaurant' },
    ],
  },
  'Türkische Küche': {
    primarySearch: { keywords: ['türkisches restaurant', 'turkish restaurant'], type: 'restaurant' },
    fallbackSearches: [
      { keywords: ['ocakbasi', 'lahmacun'], type: 'restaurant' },
    ],
  },
};

// ─── Yardımcı fonksiyonlar ─────────────────────────────────────────────────

/** Verilen ms kadar bekle */
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

/** Radius'a göre hedef lead sayısı */
function getTargetCount(radiusKm) {
  const r = parseFloat(radiusKm) || 5;
  if (r <= 3) return 10;
  if (r <= 10) return 20;
  if (r <= 25) return 40;
  return 60;
}

/** Radius'a göre kaç sayfa çekileceği (max 3) */
function getMaxPages(radiusKm) {
  const r = parseFloat(radiusKm) || 5;
  if (r <= 5) return 1;   // 20 results per keyword
  if (r <= 15) return 2;  // 40 results per keyword
  return 3;               // 60 results per keyword
}

/** maps-proxy çağrısı (oturum token'ı zorunlu) */
const mapsProxy = async (action, params) => {
  const token = await getAccessToken();
  const response = await fetch('/api/maps-proxy', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ action, params }),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || `Maps proxy error (${response.status})`);
  return data;
};

/** Place ID → telefon/web detayları (sadece import sırasında kullanılır) */
const fetchPlaceDetails = async (placeId) => {
  try {
    const data = await mapsProxy('details', {
      place_id: placeId,
      fields: 'formatted_phone_number,website,opening_hours,formatted_address',
      language: 'de',
    });
    if (data.status === 'OK' && data.result) return data.result;
    return null;
  } catch {
    return null;
  }
};

/** Tek bir searchConfig ile Google Places araması yap, tüm keyword'leri dene */
async function executeSearchRound(searchConfig, { useGPS, gpsCoords, radius, location }) {
  const { keywords, type } = searchConfig;
  const maxPages = getMaxPages(radius);
  const allPlaces = [];
  const seenIds = new Set();

  for (const keyword of keywords) {
    try {
      let data;
      if (useGPS && gpsCoords) {
        const params = {
          location: `${gpsCoords.lat},${gpsCoords.lng}`,
          radius: Math.round(parseFloat(radius) * 1000),
          keyword,
          language: 'de',
          _maxPages: String(maxPages),
        };
        if (type) params.type = type;
        data = await mapsProxy('nearbysearch_paginated', params);
      } else {
        const params = {
          query: `${keyword} in ${location}`,
          language: 'de',
          _maxPages: String(maxPages),
        };
        if (type) params.type = type;
        data = await mapsProxy('textsearch_paginated', params);
      }
      if (data.status === 'OK' && data.results) {
        for (const p of data.results) {
          if (!seenIds.has(p.place_id)) {
            seenIds.add(p.place_id);
            allPlaces.push(p);
          }
        }
      }
    } catch { /* devam et, diğer keyword'leri dene */ }
    if (allPlaces.length >= 50) break;
  }
  return allPlaces;
}

/**
 * Alman adres formatını ayrıştırır: "Straße Nr, PLZ Stadt, Land"
 * @returns {{street: string, postal_code: string, city: string}}
 */
function parseGermanAddress(formatted) {
  let street = '', city = '', postal_code = '';
  if (!formatted) return { street, postal_code, city };
  const parts = formatted.split(',').map(s => s.trim());
  street = parts[0] || '';
  // İkinci parçadan PLZ + Stadt (ör. "80331 München")
  if (parts[1]) {
    const plzMatch = parts[1].match(/^(\d{5})\s+(.+)/);
    if (plzMatch) {
      postal_code = plzMatch[1];
      city = plzMatch[2];
    } else {
      city = parts[1];
    }
  }
  return { street, postal_code, city };
}

/** Google Places sonucunu Lead objesine dönüştür (detay çağrısı olmadan) */
function mapPlaceToLead(p, branche, location, agentEmail) {
  let street = '', city = '', postal_code = '';
  if (p.formatted_address) {
    // textsearch: tam adres döner
    ({ street, postal_code, city } = parseGermanAddress(p.formatted_address));
  } else if (p.vicinity) {
    // nearbysearch (GPS modu): adres `vicinity` alanındadır ("Straße Nr, Stadt"),
    // formatted_address YOK — aksi halde tüm firmalar agent'ın konumunu gösterir.
    const parts = p.vicinity.split(',').map(s => s.trim());
    if (parts.length > 1) {
      street = parts.slice(0, -1).join(', ');
      city = parts[parts.length - 1];
    } else {
      // Tek parça genelde sadece semt/şehirdir
      city = parts[0] || '';
    }
  }
  return {
    name: p.name,
    strasse: street,
    plz: postal_code,
    ort: city || location,
    land: 'Deutschland',
    telefon: '',  // wird beim Import nachgeladen
    website: '',  // wird beim Import nachgeladen
    branche: BRANCHEN_MAP[branche.toLowerCase()] || branche,
    status: 'Potenzieller Interessent',
    assigned_agent: agentEmail,
    google_place_id: p.place_id,
  };
}

// ─── Ana Component ─────────────────────────────────────────────────────────

export default function AILeadScout({ agentEmail, onComplete }) {
  const [selectedBranches, setSelectedBranches] = useState(new Set());
  const [location, setLocation] = useState('München Zentrum');
  const [useGPS, setUseGPS] = useState(false);
  const [gpsCoords, setGpsCoords] = useState(null);
  const [radius, setRadius] = useState('5');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [statusMsg, setStatusMsg] = useState('');
  const [error, setError] = useState('');
  const [mobileStep, setMobileStep] = useState(1);

  const queryClient = useQueryClient();

  const targetCount = getTargetCount(radius);

  const createLeadMutation = useMutation({
    mutationFn: (data) => base44.entities.Lead.create(data),
    onSuccess: () => queryClient.invalidateQueries(['agent-leads']),
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => queryClient.invalidateQueries(['agent-tasks']),
  });

  // ── GPS ──────────────────────────────────────────────────────────────────
  const getGPSLocation = async () => {
    setStatusMsg('Ermittle Standort...');
    try {
      const position = await new Promise((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject)
      );
      const { latitude, longitude } = position.coords;
      try {
        const data = await mapsProxy('geocode', { latlng: `${latitude},${longitude}`, language: 'de' });
        const result = data.results?.[0];
        if (result) {
          setLocation(result.formatted_address);
          setGpsCoords({ lat: latitude, lng: longitude });
          setUseGPS(true);
          setStatusMsg('');
          return;
        }
      } catch { /* ignore geocode failure, fall through */ }
      setLocation(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
      setGpsCoords({ lat: latitude, lng: longitude });
      setUseGPS(true);
      setStatusMsg('');
    } catch (err) {
      setError('GPS Fehler: ' + err.message);
      setStatusMsg('');
    }
  };

  // ── Hauptsuche (Multi-Round Fallback Strategie) ──────────────────────────
  const handleSearch = useCallback(async () => {
    if (selectedBranches.size === 0 || (!location && !useGPS)) {
      setError('Bitte Branche und Ort wählen.');
      return;
    }

    setIsSearching(true);
    setError('');
    setResults([]);
    setSelectedLeads(new Set());

    const MIN_PER_BRANCH = 10;
    const branches = Array.from(selectedBranches);
    const allNewLeads = [];
    const searchCtx = { useGPS, gpsCoords, radius, location };

    /** Direkt textsearch verilerinden lead oluştur (detay API çağrısı YOK — anında) */
    function processPlaces(places, branch, freshLeads, sessionPlaceIds, limit) {
      let count = 0;
      const validPlaces = places.filter(p => p.place_id && !sessionPlaceIds.has(p.place_id));

      for (const place of validPlaces) {
        if (count >= limit) break;
        if (sessionPlaceIds.has(place.place_id)) continue;

        const lead = mapPlaceToLead(place, branch, location, agentEmail);

        const isDupInDB = findDuplicateLead(lead, freshLeads);
        const isDupInSession = allNewLeads.some(l => l.google_place_id === lead.google_place_id);

        if (isDupInDB || isDupInSession) continue;

        const score = calculateLeadScore(lead, freshLeads, [], []);
        const category = categorizeByScore(score);

        allNewLeads.push({ ...lead, lead_score: score, priority_category: category });
        sessionPlaceIds.add(lead.google_place_id);
        count++;
      }

      setStatusMsg(`"${branch}": ${count} neue Kontakte`);
      return count;
    }

    try {
      // ── 1. Aktuellen Datenbestand laden (Cross-Agent Duplikat-Check) ──
      setStatusMsg('Prüfe bestehende Kontakte...');
      let freshLeads = [];
      try {
        freshLeads = await base44.entities.Lead.list();
      } catch (e) {
        console.warn('Lead list fetch failed, proceeding without fresh leads:', e);
      }

      const sessionPlaceIds = new Set();

      // ── 2. Alle Branchen PARALLEL suchen ──
      setStatusMsg(`Suche ${branches.length} Branchen parallel...`);

      /** Einzelne Branche suchen (primary + fallback parallel) */
      async function searchBranch(branch) {
        const config = BRANCH_SEARCH_CONFIG[branch];
        const branchLeads = [];
        const branchSeenIds = new Set();

        // ── Alle Suchrunden parallel starten (primary + alle fallbacks) ──
        const searchPromises = [];

        if (config) {
          // Primary search
          searchPromises.push(executeSearchRound(config.primarySearch, searchCtx));
          // Fallback searches - alle sofort starten
          if (config.fallbackSearches) {
            for (const fb of config.fallbackSearches) {
              searchPromises.push(executeSearchRound(fb, searchCtx));
            }
          }
        } else {
          // Unbekannte Branche: einfache Suche
          const genericSearch = async () => {
            try {
              const data = useGPS && gpsCoords
                ? await mapsProxy('nearbysearch_paginated', {
                  location: `${gpsCoords.lat},${gpsCoords.lng}`,
                  radius: Math.round(parseFloat(radius) * 1000),
                  keyword: branch, language: 'de', _maxPages: String(getMaxPages(radius)),
                })
                : await mapsProxy('textsearch_paginated', {
                  query: `${branch} in ${location}`, language: 'de', _maxPages: String(getMaxPages(radius)),
                });
              return (data.status === 'OK') ? (data.results || []) : [];
            } catch { return []; }
          };
          searchPromises.push(genericSearch());
        }

        // Alle Suchrunden gleichzeitig ausführen
        const roundResults = await Promise.all(searchPromises);

        // Ergebnisse zusammenführen (Duplikate per place_id entfernen)
        const allBranchPlaces = [];
        for (const places of roundResults) {
          for (const p of places) {
            if (!branchSeenIds.has(p.place_id)) {
              branchSeenIds.add(p.place_id);
              allBranchPlaces.push(p);
            }
          }
        }

        // Leads direkt aus textsearch-Ergebnissen erstellen (KEINE Detail-API → sofort)
        setStatusMsg(`"${branch}": verarbeite ${allBranchPlaces.length} Ergebnisse...`);
        const count = processPlaces(
          allBranchPlaces, branch, freshLeads, sessionPlaceIds, MIN_PER_BRANCH
        );
        setStatusMsg(`"${branch}": ${count} neue Kontakte gefunden`);
        return count;
      }

      // Alle Branchen gleichzeitig starten
      const branchResults = await Promise.allSettled(
        branches.map(branch => searchBranch(branch))
      );

      // Ergebnisse loggen
      branchResults.forEach((result, i) => {
        const branch = branches[i];
        if (result.status === 'fulfilled') {
          console.log(`Branch "${branch}": ${result.value} leads found`);
        } else {
          console.warn(`Branch "${branch}" failed:`, result.reason);
        }
      });

      allNewLeads.sort((a, b) => b.lead_score - a.lead_score);
      setResults([...allNewLeads]);
      if (allNewLeads.length > 0) setMobileStep(3);

      // ── 3. Telefon/Website Details im Hintergrund nachladen ──
      setStatusMsg('Lade Kontaktdaten...');
      const DETAIL_BATCH = 5;
      for (let i = 0; i < allNewLeads.length; i += DETAIL_BATCH) {
        const batch = allNewLeads.slice(i, i + DETAIL_BATCH);
        const detailResults = await Promise.allSettled(
          batch.map(lead => fetchPlaceDetails(lead.google_place_id))
        );
        let updated = false;
        detailResults.forEach((res, j) => {
          if (res.status === 'fulfilled' && res.value) {
            const idx = i + j;
            if (res.value.formatted_phone_number) {
              allNewLeads[idx].telefon = res.value.formatted_phone_number;
              updated = true;
            }
            if (res.value.website) {
              allNewLeads[idx].website = res.value.website;
              updated = true;
            }
            if (res.value.opening_hours) {
              allNewLeads[idx].opening_hours = res.value.opening_hours;
              updated = true;
            }
            // nearbysearch sonuçlarında PLZ yoktur — tam adresle tamamla
            if (res.value.formatted_address) {
              const { street, postal_code, city } = parseGermanAddress(res.value.formatted_address);
              if (street) allNewLeads[idx].strasse = street;
              if (postal_code) allNewLeads[idx].plz = postal_code;
              if (city) allNewLeads[idx].ort = city;
              updated = true;
            }
          }
        });
        if (updated) setResults([...allNewLeads]);
        setStatusMsg(`Kontaktdaten: ${Math.min(i + DETAIL_BATCH, allNewLeads.length)}/${allNewLeads.length}`);
      }
      setStatusMsg('');

    } catch (err) {
      console.error(err);
      setError('Fehler bei der Suche: ' + err.message);
    } finally {
      setIsSearching(false);
    }
  }, [selectedBranches, location, useGPS, gpsCoords, radius, agentEmail]);

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    const leadsToImport = results.filter((_, i) => selectedLeads.has(i));
    if (leadsToImport.length === 0) return;

    try {
      setStatusMsg(`Importiere ${leadsToImport.length} Kontakte...`);

      // Letzter frischer DB-Check vor Import (Race-Condition verhindern)
      const latestLeads = await base44.entities.Lead.list();
      let importedCount = 0;
      let skippedCount = 0;

      for (const leadData of leadsToImport) {
        const isDuplicate = findDuplicateLead(leadData, latestLeads);
        if (isDuplicate) { skippedCount++; continue; }

        // Telefon/Website Details nachladen (nur für ausgewählte Leads)
        setStatusMsg(`Details laden: ${leadData.name}...`);
        let enriched = { ...leadData };
        try {
          const detail = await fetchPlaceDetails(leadData.google_place_id);
          if (detail) {
            enriched.telefon = detail.formatted_phone_number || '';
            enriched.website = detail.website || '';
            enriched.opening_hours = detail.opening_hours || null;
            if (detail.formatted_address) {
              const { street, postal_code, city } = parseGermanAddress(detail.formatted_address);
              if (street) enriched.strasse = street;
              if (postal_code) enriched.plz = postal_code;
              if (city) enriched.ort = city;
            }
          }
        } catch { /* Details-Fehler ignorieren, Lead trotzdem importieren */ }

        const createdLead = await createLeadMutation.mutateAsync({
          ...enriched,
          assigned_agent: agentEmail,
          status: 'Potenzieller Interessent',
        });

        if (createdLead) {
          await createTaskMutation.mutateAsync({
            lead_id: createdLead.id,
            lead_name: leadData.name,
            lead_address: [leadData.strasse, leadData.plz, leadData.ort].filter(Boolean).join(', '),
            lead_opening_hours: enriched.opening_hours,
            task_type: 'Besuchen',
            typ: 'Besuchen',
            due_date: new Date().toISOString().split('T')[0],
            status: 'Offen',
            info_block: 'Erstkontakt – Google Maps Scout',
            assigned_agent: agentEmail,
          });
          importedCount++;
        }
      }

      if (importedCount > 0) notifyBulkLeadImport(importedCount, agentEmail);

      onComplete?.();
      setResults([]);
      setSelectedLeads(new Set());
      setSelectedBranches(new Set());
      setLocation('');
      setMobileStep(1);

      if (skippedCount > 0) {
        toast.success(`${importedCount} importiert. ${skippedCount} übersprungen (Duplikate).`);
      } else {
        toast.success(`${importedCount} Kontakte erfolgreich importiert.`);
      }
    } catch (err) {
      setError('Fehler beim Import: ' + err.message);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggleBranch = (b) => {
    const s = new Set(selectedBranches);
    s.has(b) ? s.delete(b) : s.add(b);
    setSelectedBranches(s);
  };

  const toggleLead = (i) => {
    const s = new Set(selectedLeads);
    s.has(i) ? s.delete(i) : s.add(i);
    setSelectedLeads(s);
  };

  // ── Branş seçici (paylaşılan) ─────────────────────────────────────────────
  const branchSelector = (cols = 2) => (
    <div className={`grid grid-cols-${cols} gap-2`}>
      {DISPLAY_BRANCHES.map((branche) => (
        <div
          key={branche}
          onClick={() => toggleBranch(branche)}
          className={`cursor-pointer px-3 py-3 rounded-xl text-xs font-medium transition-all text-center border select-none active:scale-95
            ${selectedBranches.has(branche)
              ? 'bg-blue-50 text-blue-700 border-blue-300 shadow-sm ring-1 ring-blue-200'
              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}
        >
          {branche}
        </div>
      ))}
    </div>
  );

  // ── Standort-Eingabe (paylaşılan) ─────────────────────────────────────────
  const locationInput = (large = false) => (
    <div className="space-y-3">
      <div className="relative">
        <Input
          placeholder="Stadt oder PLZ..."
          value={location}
          onChange={(e) => { setLocation(e.target.value); setUseGPS(false); setGpsCoords(null); }}
          className={`pr-10 ${large ? 'h-12 text-base' : ''}`}
        />
        <Button
          type="button" variant="ghost" size="icon"
          className="absolute right-0 top-0 h-full text-slate-400 hover:text-blue-600"
          onClick={getGPSLocation} title="GPS Standort verwenden"
        >
          <MapPin className={large ? 'w-5 h-5' : 'w-4 h-4'} />
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <Input
          type="number" value={radius}
          onChange={(e) => setRadius(e.target.value)}
          min="1" max="50"
          className={`flex-1 ${large ? 'h-12 text-base' : ''}`}
        />
        <span className="text-sm text-slate-500 font-medium whitespace-nowrap">km Radius</span>
      </div>
      {/* Ziel-Anzeige */}
      <div className="flex items-center gap-2 p-2.5 bg-blue-50 rounded-lg">
        <Target className="w-4 h-4 text-blue-500 shrink-0" />
        <p className="text-xs text-blue-700">
          Ziel: <strong>~{targetCount} neue Kontakte</strong> pro Branche
        </p>
      </div>
    </div>
  );

  // ── Ergebniskarte (paylaşılan) ────────────────────────────────────────────
  const resultCard = (lead, index) => (
    <Card
      key={index}
      onClick={() => toggleLead(index)}
      className={`p-4 transition-all active:scale-[0.98] cursor-pointer ${selectedLeads.has(index)
        ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-200'
        : 'bg-white border-slate-200'
        }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0 mr-3">
          <h4 className="font-semibold text-slate-900 text-base">{lead.name}</h4>
          <p className="text-sm text-slate-500 mt-0.5">{lead.branche}</p>
        </div>
        <Checkbox checked={selectedLeads.has(index)} onCheckedChange={() => toggleLead(index)} className="h-6 w-6" />
      </div>
      <div className="flex items-center gap-3 mb-3">
        <Badge variant="secondary" className={`text-xs px-2 h-5 border ${getCategoryColor(lead.priority_category)}`}>
          {getCategoryIcon(lead.priority_category)} {lead.priority_category}
        </Badge>
        <div className="flex items-center gap-2 flex-1">
          <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${lead.lead_score}%` }} />
          </div>
          <span className="text-xs font-mono font-bold text-slate-600">{lead.lead_score}</span>
        </div>
      </div>
      <div className="space-y-1.5 pt-2 border-t border-slate-100">
        <div className="flex items-start gap-2 text-sm text-slate-600">
          <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
          <span className="leading-snug">{[lead.strasse, lead.plz, lead.ort].filter(Boolean).join(', ')}</span>
        </div>
        {lead.telefon && (
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Phone className="w-4 h-4 text-slate-400 shrink-0" />
            <span className="font-mono">{lead.telefon}</span>
          </div>
        )}
      </div>
    </Card>
  );

  // ════════════════════════════════════════════════════════════════════════
  // MOBILE WIZARD
  // ════════════════════════════════════════════════════════════════════════
  const renderMobileWizard = () => (
    <div className="md:hidden flex flex-col h-[80vh]">
      {/* Step Indicator */}
      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b">
        {[1, 2, 3].map(step => (
          <div key={step} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${mobileStep === step ? 'bg-blue-600 text-white'
              : mobileStep > step ? 'bg-emerald-500 text-white'
                : 'bg-slate-200 text-slate-500'
              }`}>
              {mobileStep > step ? '✓' : step}
            </div>
            <span className={`text-xs font-medium ${mobileStep === step ? 'text-slate-900' : 'text-slate-400'}`}>
              {step === 1 ? 'Standort' : step === 2 ? 'Branche' : 'Ergebnisse'}
            </span>
            {step < 3 && <div className="flex-1 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Schritt 1: Standort */}
        {mobileStep === 1 && (
          <div className="p-4 space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Standort & Radius</h3>
              <p className="text-sm text-slate-500 mt-1">Wo sollen wir nach Geschäften suchen?</p>
            </div>
            {locationInput(true)}
            {statusMsg && <p className="text-sm text-blue-600">{statusMsg}</p>}
          </div>
        )}

        {/* Schritt 2: Branchen */}
        {mobileStep === 2 && (
          <div className="p-4 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Branchen wählen</h3>
              <p className="text-sm text-slate-500 mt-1">Welche Branchen interessieren Sie?</p>
            </div>
            {branchSelector(2)}
          </div>
        )}

        {/* Schritt 3: Ergebnisse */}
        {mobileStep === 3 && (
          <div className="pb-24">
            {results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400 px-4">
                <LayoutList className="w-12 h-12 mb-3 opacity-40" />
                <p className="text-slate-900 font-medium mb-1">Keine neuen Kontakte gefunden</p>
                <p className="text-sm text-center">Alle Ergebnisse sind bereits in Ihrem System.</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setMobileStep(1)}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Neue Suche
                </Button>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-700">{results.length} neue Kontakte</p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setSelectedLeads(new Set())}>Keine</Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedLeads(new Set(results.map((_, i) => i)))}>Alle</Button>
                  </div>
                </div>
                {results.map((lead, index) => resultCard(lead, index))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mobile Bottom Actions */}
      <div className="sticky bottom-0 bg-white border-t px-4 py-3 safe-area-inset-bottom">
        {error && (
          <div className="flex items-start gap-2 text-red-600 text-xs mb-2 p-2 bg-red-50 rounded-lg">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {mobileStep === 1 && (
          <Button onClick={() => setMobileStep(2)} disabled={!location} className="w-full h-12">
            Weiter zu Branchen <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        )}

        {mobileStep === 2 && (
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMobileStep(1)} className="h-12">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleSearch}
              disabled={isSearching || selectedBranches.size === 0 || !location}
              className="flex-1 h-12 bg-slate-900 hover:bg-slate-800"
            >
              {isSearching ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{statusMsg || 'Suche...'}</>
              ) : (
                <><Search className="w-4 h-4 mr-2" />Suche ({selectedBranches.size}) – ~{targetCount}/Branche</>
              )}
            </Button>
          </div>
        )}

        {mobileStep === 3 && (
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setMobileStep(2)} className="h-12">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleImport}
              disabled={selectedLeads.size === 0}
              className="flex-1 h-12 bg-emerald-600 hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {selectedLeads.size} importieren
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════
  // DESKTOP VIEW
  // ════════════════════════════════════════════════════════════════════════
  const renderDesktopView = () => (
    <div className="hidden md:flex flex-col h-[85vh] -m-6 bg-slate-50/50">
      {/* Header */}
      <header className="px-6 py-4 bg-white border-b flex items-center justify-between shadow-sm z-10">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg text-blue-700">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Google Places Scout</h2>
            <p className="text-xs text-slate-500">Live-Daten von Google Maps · Agent-isoliert</p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-amber-700 text-sm font-medium max-w-md bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="truncate">{error}</span>
          </div>
        )}

        {results.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-600 font-medium">{selectedLeads.size} ausgewählt</span>
            <Button variant="ghost" size="sm" onClick={() => setSelectedLeads(new Set())}>Keine</Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedLeads(new Set(results.map((_, i) => i)))}>Alle</Button>
            <div className="h-6 w-px bg-slate-200 mx-1" />
            <Button
              onClick={handleImport}
              disabled={selectedLeads.size === 0}
              className="bg-emerald-600 hover:bg-emerald-700 shadow-sm"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Importieren ({selectedLeads.size})
            </Button>
          </div>
        )}
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Sidebar */}
        <aside className="w-[300px] bg-white border-r flex flex-col shrink-0 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Standort */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <MapPin className="w-4 h-4 text-slate-500" /> Standort & Radius
              </div>
              {locationInput(false)}
            </div>

            <div className="h-px bg-slate-100" />

            {/* Branchen */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Filter className="w-4 h-4 text-slate-500" /> Branchen
              </div>
              {branchSelector(2)}
            </div>
          </div>

          <div className="mt-auto p-6 border-t bg-slate-50">
            <Button
              onClick={handleSearch}
              disabled={isSearching || selectedBranches.size === 0 || !location}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11"
            >
              {isSearching ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{statusMsg || 'Lade...'}</>
              ) : (
                <><Search className="w-4 h-4 mr-2" />Suche starten ({selectedBranches.size} Branchen)</>
              )}
            </Button>
          </div>
        </aside>

        {/* Ergebnisse */}
        <main className="flex-1 flex flex-col bg-slate-50/50 overflow-hidden">
          {results.length === 0 && !isSearching ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 text-slate-400">
              <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center mb-4">
                <LayoutList className="w-8 h-8 text-slate-300" />
              </div>
              <h3 className="text-slate-900 font-medium mb-1">Noch keine Ergebnisse</h3>
              <p className="text-sm max-w-xs mx-auto">
                Wähle Branchen und Standort – der Scout sucht <strong>{targetCount} neue Kontakte</strong> pro Branche, die noch kein anderer Agent im System hat.
              </p>
            </div>
          ) : isSearching ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-4 text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
              <p className="font-medium text-slate-700">{statusMsg || 'Suche läuft...'}</p>
              <p className="text-sm text-slate-400 max-w-xs text-center">
                Lade bis zu {targetCount} neue Kontakte pro Branche ({getMaxPages(radius)} Seiten).
              </p>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left text-sm border-separate border-spacing-0">
                <thead className="bg-slate-50/90 backdrop-blur sticky top-0 z-10 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 border-b border-slate-200 w-12 text-center">
                      <Checkbox
                        checked={selectedLeads.size === results.length && results.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) setSelectedLeads(new Set(results.map((_, i) => i)));
                          else setSelectedLeads(new Set());
                        }}
                      />
                    </th>
                    <th className="px-4 py-3 font-medium text-slate-500 border-b border-slate-200">Geschäft</th>
                    <th className="px-4 py-3 font-medium text-slate-500 border-b border-slate-200">Branche</th>
                    <th className="px-4 py-3 font-medium text-slate-500 border-b border-slate-200">Score</th>
                    <th className="px-4 py-3 font-medium text-slate-500 border-b border-slate-200">Adresse</th>
                    <th className="px-4 py-3 font-medium text-slate-500 border-b border-slate-200">Telefon</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {results.map((lead, index) => (
                    <tr
                      key={index}
                      className={`transition-colors hover:bg-slate-50 cursor-pointer ${selectedLeads.has(index) ? 'bg-blue-50/40' : ''}`}
                      onClick={() => toggleLead(index)}
                    >
                      <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                        <Checkbox checked={selectedLeads.has(index)} onCheckedChange={() => toggleLead(index)} />
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-slate-900">{lead.name}</div>
                        <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 h-5 border mt-1 ${getCategoryColor(lead.priority_category)}`}>
                          {getCategoryIcon(lead.priority_category)} {lead.priority_category}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-slate-600">{lead.branche}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${lead.lead_score}%` }} />
                          </div>
                          <span className="text-xs font-mono text-slate-500">{lead.lead_score}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        <span className="flex items-center gap-1.5" title={[lead.strasse, lead.plz, lead.ort].join(', ')}>
                          <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="truncate max-w-[260px]">{[lead.strasse, lead.ort].filter(Boolean).join(', ')}</span>
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-600">
                        {lead.telefon && <span className="text-sm font-mono">{lead.telefon}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    </div>
  );

  return (
    <div className="relative">
      {renderMobileWizard()}
      {renderDesktopView()}
    </div>
  );
}
